/**
 * Create or edit a project. A new project can start from an accepted offer
 * (`?offerId=`): customer, title, currency and hours are taken over, the
 * offer's request becomes the project's source and the offer is linked.
 */
import { useState, type ChangeEvent, type FormEvent } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { InputText as Input } from "@primereact/ui/inputtext";
import { Textarea } from "@primereact/ui/textarea";
import type { CustomerResponse, OfferResponse } from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import { PageHeader } from "../../components/ui/PageHeader.js";
import { DateInput, SelectInput } from "../../components/ui/controls.js";
import { apiGet, apiGetPage, apiPatch, apiPost } from "../../lib/api.js";
import { CustomerPicker, useCustomer } from "../commercial/document.js";
import { ErrorPanel, LoadingPanel, MutationError, Section } from "../shared.js";
import type { ProjectDetail, ProjectListNode } from "./types.js";

type Role = "none" | "member" | "manager";
type FormState = {
  customerId: string;
  parentId: string;
  title: string;
  description: string;
  priority: "low" | "normal" | "high";
  plannedStart: string;
  plannedEnd: string;
  plannedHours: string;
  currency: "CHF" | "EUR" | "USD";
  internalNotes: string;
  roles: Record<string, Role>;
};

const EMPTY: FormState = {
  customerId: "",
  parentId: "",
  title: "",
  description: "",
  priority: "normal",
  plannedStart: "",
  plannedEnd: "",
  plannedHours: "0",
  currency: "CHF",
  internalNotes: "",
  roles: {},
};

/** Hours sold on the offer: quantities of lines measured in hours. */
function offerHours(offer: OfferResponse) {
  return offer.items
    .filter((item) => ["h", "std", "hour", "hours"].includes(item.unit))
    .reduce((sum, item) => sum + Number(item.quantity), 0);
}

function fromProject(project: ProjectDetail): FormState {
  return {
    ...EMPTY,
    customerId: project.customerId,
    parentId: project.parentId ?? "",
    title: project.title,
    description: project.description ?? "",
    priority: project.priority as FormState["priority"],
    plannedStart: project.plannedStart?.slice(0, 10) ?? "",
    plannedEnd: project.plannedEnd?.slice(0, 10) ?? "",
    plannedHours: String(project.plannedMinutes / 60),
    currency: project.currency as FormState["currency"],
    internalNotes: project.internalNotes ?? "",
  };
}

export function ProjectFormPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const offerId = id ? null : params.get("offerId");
  const project = useQuery({
    queryKey: ["project", id],
    queryFn: () =>
      apiGet<{ data: ProjectDetail }>(`/projects/${id}`).then(
        (response) => response.data,
      ),
    enabled: Boolean(id),
  });
  const offer = useQuery({
    queryKey: ["offer", offerId],
    queryFn: () =>
      apiGet<{ data: OfferResponse }>(`/offers/${offerId}`).then(
        (response) => response.data,
      ),
    enabled: Boolean(offerId),
  });
  if (project.isLoading || offer.isLoading) return <LoadingPanel />;
  if (project.error || offer.error)
    return <ErrorPanel error={project.error || offer.error} />;
  const initial: FormState = project.data
    ? fromProject(project.data)
    : offer.data
      ? {
          ...EMPTY,
          customerId: offer.data.customer.id,
          title: offer.data.title,
          currency: offer.data.currency as FormState["currency"],
          plannedHours: String(offerHours(offer.data)),
        }
      : { ...EMPTY, customerId: params.get("customerId") ?? "" };
  return (
    <ProjectForm
      key={id ?? offerId ?? "new"}
      initial={initial}
      project={project.data ?? null}
      offer={offer.data ?? null}
    />
  );
}

function ProjectForm({
  initial,
  project,
  offer,
}: {
  initial: FormState;
  project: ProjectDetail | null;
  offer: OfferResponse | null;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [form, setForm] = useState(initial);
  const [customer, setCustomer] = useState<CustomerResponse | null>(null);
  const preselected = useCustomer(
    !project && !customer && form.customerId ? form.customerId : null,
  );
  const selectedCustomer = customer ?? preselected.data ?? null;
  const parents = useQuery({
    queryKey: ["project-parents", form.customerId],
    enabled: Boolean(form.customerId),
    queryFn: () =>
      apiGetPage<ProjectListNode>("/projects", {
        customerId: form.customerId,
        pageSize: 100,
      }),
  });
  const candidates = useQuery({
    queryKey: ["project-member-candidates", "create"],
    enabled: !project,
    queryFn: () =>
      apiGet<{
        data: Array<{
          id: string;
          firstName: string;
          lastName: string;
          email: string;
        }>;
      }>("/projects/member-candidates").then((response) => response.data),
  });
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  const plannedMinutes = Math.round(Number(form.plannedHours || 0) * 60);

  const save = useMutation({
    mutationFn: async () => {
      if (project) {
        await apiPatch(`/projects/${project.id}`, {
          version: project.version,
          parentId: form.parentId || null,
          title: form.title,
          description: form.description || null,
          priority: form.priority,
          plannedStart: form.plannedStart || null,
          plannedEnd: form.plannedEnd || null,
          plannedMinutes,
          internalNotes: form.internalNotes || null,
        });
        return project.id;
      }
      const roleIds = (role: Role) =>
        Object.keys(form.roles).filter((userId) => form.roles[userId] === role);
      const created = await apiPost<{
        data: ProjectDetail | { project: ProjectDetail };
      }>("/projects", {
        customerId: form.customerId,
        ...(form.parentId ? { parentId: form.parentId } : {}),
        title: form.title,
        ...(form.description ? { description: form.description } : {}),
        priority: form.priority,
        ...(form.plannedStart ? { plannedStart: form.plannedStart } : {}),
        ...(form.plannedEnd ? { plannedEnd: form.plannedEnd } : {}),
        plannedMinutes,
        currency: form.currency,
        ...(form.internalNotes ? { internalNotes: form.internalNotes } : {}),
        ...(offer ? { sourceOfferId: offer.id } : {}),
        managerIds: roleIds("manager"),
        memberIds: roleIds("member"),
      });
      return "project" in created.data
        ? created.data.project.id
        : created.data.id;
    },
    onSuccess: async (id) => {
      await client.invalidateQueries({ queryKey: ["projects"] });
      await client.invalidateQueries({ queryKey: ["project", id] });
      if (offer) await client.invalidateQueries({ queryKey: ["offer"] });
      navigate(`/projects/${id}`);
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };
  const parentOptions = [
    { value: "", label: t("projects.noParent") },
    ...(parents.data?.data ?? [])
      .filter((node) => node.data.id !== project?.id)
      .map((node) => ({
        value: node.data.id,
        label: `${node.data.number} · ${node.data.title}`,
      })),
  ];

  return (
    <form onSubmit={submit}>
      <PageHeader
        title={project ? t("projects.edit") : t("projects.new")}
        subtitle={
          offer
            ? t("projects.fromOffer", { number: offer.displayNumber })
            : undefined
        }
      />
      <MutationError error={save.error} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
        <Section>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {!project ? (
              <Field
                className="md:col-span-2"
                label={t("customer.singular")}
                required
              >
                <CustomerPicker
                  value={selectedCustomer}
                  disabled={Boolean(offer)}
                  onChange={(value) => {
                    setCustomer(value);
                    setForm((current) => ({
                      ...current,
                      customerId: value?.id ?? "",
                      parentId: "",
                    }));
                  }}
                />
              </Field>
            ) : null}
            <Field
              className="md:col-span-2"
              label={t("commercial.title")}
              required
            >
              <Input
                fluid
                required
                maxLength={180}
                value={form.title}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  set("title", event.target.value)
                }
              />
            </Field>
            <Field className="md:col-span-2" label={t("common.description")}>
              <Textarea
                className="w-full"
                rows={4}
                maxLength={6000}
                value={form.description}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                  set("description", event.target.value)
                }
              />
            </Field>
            <Field label={t("projects.plannedStart")}>
              <DateInput
                value={form.plannedStart}
                onChange={(value) => set("plannedStart", value)}
              />
            </Field>
            <Field label={t("projects.plannedEnd")}>
              <DateInput
                value={form.plannedEnd}
                minDate={form.plannedStart || undefined}
                onChange={(value) => set("plannedEnd", value)}
              />
            </Field>
            <Field label={t("projects.plannedHours")}>
              <Input
                fluid
                inputMode="decimal"
                value={form.plannedHours}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  set("plannedHours", event.target.value.replace(",", "."))
                }
              />
            </Field>
            <Field label={t("common.priority")}>
              <SelectInput
                value={form.priority}
                onChange={(value) => set("priority", value)}
                options={(["low", "normal", "high"] as const).map((value) => ({
                  value,
                  label: t(`priority.${value}`),
                }))}
              />
            </Field>
            <Field label={t("projects.parent")}>
              <SelectInput
                value={form.parentId}
                onChange={(value) => set("parentId", value)}
                options={parentOptions}
                placeholder={t("projects.noParent")}
                disabled={!form.customerId}
              />
            </Field>
            {!project ? (
              <Field label={t("common.currency")}>
                <SelectInput
                  value={form.currency}
                  onChange={(value) => set("currency", value)}
                  disabled={Boolean(offer)}
                  options={(["CHF", "EUR", "USD"] as const).map((code) => ({
                    value: code,
                    label: code,
                  }))}
                />
              </Field>
            ) : null}
            <Field
              className="md:col-span-2"
              label={t("projects.internalNotes")}
            >
              <Textarea
                className="w-full"
                rows={3}
                maxLength={4000}
                value={form.internalNotes}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                  set("internalNotes", event.target.value)
                }
              />
            </Field>
          </div>
        </Section>
        {!project ? (
          <Section
            title={t("projects.assignees")}
            subtitle={t("projects.assigneesHint")}
          >
            {candidates.isLoading ? (
              <LoadingPanel />
            ) : (
              <ul className="space-y-2">
                {(candidates.data ?? []).map((candidate) => (
                  <li
                    key={candidate.id}
                    className="grid grid-cols-[1fr_8.5rem] items-center gap-2 text-sm"
                  >
                    <span className="min-w-0 truncate">
                      {candidate.firstName} {candidate.lastName}
                    </span>
                    <SelectInput
                      ariaLabel={`${candidate.firstName} ${candidate.lastName}`}
                      value={form.roles[candidate.id] ?? "none"}
                      onChange={(role) =>
                        set("roles", { ...form.roles, [candidate.id]: role })
                      }
                      options={[
                        { value: "none", label: "—" },
                        { value: "member", label: t("projects.member") },
                        { value: "manager", label: t("projects.manager") },
                      ]}
                    />
                  </li>
                ))}
              </ul>
            )}
          </Section>
        ) : null}
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button
          as={Link}
          to={project ? `/projects/${project.id}` : "/projects"}
          type="button"
          variant="outlined"
          severity="secondary"
        >
          {t("common.cancel")}
        </Button>
        <Button
          type="submit"
          disabled={!form.customerId || !form.title.trim()}
          loading={save.isPending}
        >
          {t("common.save")}
        </Button>
      </div>
    </form>
  );
}
